import { NotificationStatus, SocketClientMessage, TournamentCount } from '@okletsplay/core';
import { useSocketSubscriber } from 'app/hooks/socket';
import { AppCountersContext } from 'app/providers/AppCountersProvider';
import { dispatchUnwrap } from 'app/utils/store';
import { getActiveMatchesCount } from 'modules/matches/store/matches/repository';
import { getTournamentsCount } from 'modules/tournaments/store/tournaments/repository';
import { useCallback, useContext, useEffect } from 'react';
import { getNotificationsCount } from 'modules/notifications/store/notifications/repository';
import { useKeycloak } from '@react-keycloak/web';
import { ExchangeRatePayload } from 'app/interfaces/socket/exchange-rate-payload';
import { useDispatch } from 'react-redux';
import { walletReducers } from 'modules/payments/store/wallet/slice';
import { getSingleCurrencyBalance } from 'modules/payments/store/wallet/repository';
import { getBalance } from 'modules/payments/store/user-payment-accounts/repository';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { getExchangeRates } from 'modules/payments/store/exchange-rates/repository';
import { ExchangeRateDto } from 'app/interfaces/dto';
import { exchangeRatesReducers } from 'modules/payments/store/exchange-rates/slice';

export const useAppCounters = () => {
  const { keycloak } = useKeycloak();
  const dispatch = useDispatch();

  const {
    setActiveMatchesCount,
    setUnreadNotificationsCount,
    setActiveTournamentsCount,
    setPastTournamentsCount,
    setParticipatedTournamentsCount,
  } = useContext(AppCountersContext);

  const fetchActiveMatchesCount = useCallback(() => {
    dispatchUnwrap<number>(getActiveMatchesCount(), {
      success: (count) => setActiveMatchesCount(count),
    });
  }, [setActiveMatchesCount]);

  const fetchNotificationsCount = useCallback(() => {
    dispatchUnwrap<{ count: number }>(getNotificationsCount(NotificationStatus.Unread), {
      success: ({ count }) => setUnreadNotificationsCount(count),
    });
  }, [setUnreadNotificationsCount]);

  const fetchTournamentsCount = useCallback(() => {
    dispatchUnwrap<TournamentCount>(getTournamentsCount(), {
      success: (counts) => {
        setActiveTournamentsCount(counts?.active ?? 0);
        setPastTournamentsCount(counts?.past ?? 0);
        setParticipatedTournamentsCount(counts?.participated ?? 0);
      },
    });
  }, [setActiveTournamentsCount, setPastTournamentsCount, setParticipatedTournamentsCount]);

  const fetchBalance = useCallback(() => {
    if (MultiCurrencyConfig.isAnyEnabled()) {
      dispatch(getBalance());
      return;
    }

    dispatch(getSingleCurrencyBalance());
  }, [dispatch]);

  const fetchExchangeRates = useCallback(() => {
    if (!MultiCurrencyConfig.isAnyEnabled()) {
      return;
    }

    dispatch(getExchangeRates());
  }, [dispatch]);

  const updateExchangeRate = (exchangeRate: ExchangeRatePayload | ExchangeRateDto[]) => {
    if (typeof exchangeRate === 'number') {
      dispatch(walletReducers.updateExchangeRate(exchangeRate));
      return;
    }

    dispatch(exchangeRatesReducers.updateExchangeRates(exchangeRate));
  };

  useSocketSubscriber([
    {
      action: SocketClientMessage.Match,
      handler: () => fetchActiveMatchesCount(),
    },
    {
      action: SocketClientMessage.Tournament,
      handler: () => fetchTournamentsCount(),
    },
    {
      action: SocketClientMessage.Notification,
      handler: () => fetchNotificationsCount(),
    },
    {
      action: SocketClientMessage.BalanceUpdated,
      handler: () => {
        fetchBalance();
      },
    },
  ]);

  useSocketSubscriber<ExchangeRatePayload | ExchangeRateDto[]>([
    {
      action: SocketClientMessage.ExchangeRate,
      handler: updateExchangeRate,
    },
  ]);

  useEffect(() => {
    if (keycloak?.authenticated) {
      fetchActiveMatchesCount();
      fetchTournamentsCount();
      fetchNotificationsCount();
      fetchBalance();
    }

    fetchExchangeRates();
  }, [
    fetchBalance,
    fetchActiveMatchesCount,
    fetchNotificationsCount,
    fetchTournamentsCount,
    keycloak?.authenticated,
    fetchExchangeRates,
  ]);
};
